package cc.bluepeak.clubmanager.site.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import cc.bluepeak.clubmanager.site.styles.Theme
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.onAnimationEnd
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.lucide.LucideMinus
import com.varabyte.kobweb.silk.components.icons.lucide.LucidePlus
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2

val PanelKeyframes = Keyframes {
    from { Modifier.maxHeight(0.px) }
    to { Modifier.maxHeight(5.cssRem) }
}

val PanelReverseKeyframes = Keyframes {
    from { Modifier.maxHeight(5.cssRem) }
    to { Modifier.maxHeight(0.px) }
}

val PanelStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().color(Theme.secondary).fontSize(.9.cssRem)
            .overflow(Overflow.Hidden)
            .padding(leftRight = 8.px)
            .animation(PanelKeyframes.toAnimation(400.ms, AnimationTimingFunction.EaseIn))
    }
}

data class AccordionItem(val title: String, val content: @Composable ColumnScope.() -> Unit)

@Composable
fun Accordion(items: List<AccordionItem>) {
    var activeIndex by remember { mutableStateOf(-1) }
    var leavingIndex by remember { mutableStateOf(-1) }
    Column(Modifier.fillMaxWidth()) {
        items.forEachIndexed { index, item ->
            Column(
                Modifier.fillMaxWidth().padding(leftRight = 1.cssRem).backgroundColor(Colors.Transparent)
                    .borderRadius(5.px)
                    .thenIf(activeIndex == index, Modifier.backgroundColor(Theme.primary.copyf(alpha = .1f)))
            ) {
                H2(Modifier.margin(0.px).fillMaxWidth().toAttrs()) {
                    Button(
                        {
                            leavingIndex = activeIndex
                            activeIndex = if (activeIndex == index) -1 else index
                        },
                        Modifier.fillMaxWidth().padding(0.px).color(Theme.secondary).backgroundColor(Colors.Transparent)
                            .thenIf(activeIndex == index, Modifier.color(Theme.primary)),
                        UncoloredButtonVariant
                    ) {
                        Row(
                            Modifier.fillMaxWidth().whiteSpace(WhiteSpace.BreakSpaces),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            SpanText(item.title)
                            if (activeIndex == index) LucideMinus() else LucidePlus()
                        }
                    }
                }
                if (activeIndex == index || leavingIndex == index || AppGlobals.isExporting)
                    Column(
                        PanelStyle.toModifier().thenIf(
                            leavingIndex == index,
                            Modifier.animation(
                                PanelReverseKeyframes.toAnimation(
                                    400.ms,
                                    AnimationTimingFunction.EaseOut,
                                )
                            ).onAnimationEnd { leavingIndex = -1 }
                        )
                    ) {
                        item.content(this)
                        Div(Modifier.height(1.cssRem).toAttrs())
                    }
            }
        }
    }
}